import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import "react-quill/dist/quill.snow.css";
import presentationPlaceholder from "../../assets/presntationBG/placeholder.png";
import { tileUrlToThumbnail } from "./presentationPreview";

const STAGE_W = 1280;
const STAGE_H = 720;

function asUrl(value) {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "object") {
    return asUrl(value.url || value.thumbnailUrl || value.src || value.content);
  }
  return null;
}

function parseSize(value) {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  const n = parseFloat(String(value).replace("px", ""));
  return Number.isFinite(n) ? n : 0;
}

function mediaUrlForElement(el) {
  if (!el) return null;
  if (el.type === "SLIDE") return tileUrlToThumbnail(el.content);
  if (el.type === "IMAGE" || el.type === "VIDEO") return asUrl(el.content);
  return null;
}

/**
 * Scaled-down snapshot of one presentation slide (images + text).
 * Renders the real 1280×720 stage and scales it into the tile so text
 * layout/fonts stay correct relative to images.
 */
export default function SlideMiniPreview({ elements, empty = false }) {
  const data = Array.isArray(elements) ? elements : [];
  const hasContent = data.length > 0;
  const containerRef = useRef(null);
  const [scale, setScale] = useState(0.1);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth || 0;
      if (w > 0) setScale(w / STAGE_W);
    };
    update();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [empty, hasContent]);

  if (empty || !hasContent) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={presentationPlaceholder}
          alt=""
          sx={{
            width: "72%",
            height: "72%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: STAGE_W,
          height: STAGE_H,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          bgcolor: "#fff",
          pointerEvents: "none",
        }}
      >
        {data.map((el, idx) => {
          if (!el || el.type === "3D_MODEL") return null;

          const left = parseSize(el.position?.left);
          const top = parseSize(el.position?.top);
          const width = parseSize(el.dimension?.width) || (el.type === "TEXT" ? 150 : 100);
          const height = parseSize(el.dimension?.height) || (el.type === "TEXT" ? 40 : 100);
          const zIndex = el.isBackground ? 0 : idx + 1;

          if (el.type === "IMAGE" || el.type === "SLIDE" || el.type === "VIDEO") {
            const url = mediaUrlForElement(el);
            if (!url) return null;
            return (
              <Box
                key={el.id || idx}
                component={el.type === "VIDEO" ? "div" : "img"}
                src={el.type === "VIDEO" ? undefined : url}
                alt=""
                sx={{
                  position: "absolute",
                  left,
                  top,
                  width,
                  height,
                  zIndex,
                  overflow: "hidden",
                  objectFit: el.imageFit === "fill" ? "fill" : el.imageFit === "cover" || el.isBackground ? "cover" : "contain",
                  transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
                  transformOrigin: "center center",
                  display: "block",
                  bgcolor: el.type === "VIDEO" ? "#0f172a" : "transparent",
                  ...(el.type === "VIDEO"
                    ? {
                        backgroundImage: `url(${url})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }
                    : null),
                }}
              />
            );
          }

          if (el.type === "TEXT") {
            const html = typeof el.content === "string" ? el.content : "";
            if (!html || !html.replace(/<[^>]+>/g, "").trim()) return null;
            return (
              <Box
                key={el.id || idx}
                className="ql-editor"
                sx={{
                  position: "absolute",
                  left,
                  top,
                  width,
                  height,
                  zIndex,
                  overflow: "hidden",
                  padding: 0,
                  lineHeight: 1.15,
                  transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
                  transformOrigin: "center center",
                  color: "#0f172a",
                  "& p": { margin: 0 },
                }}
                dangerouslySetInnerHTML={{ __html: html }}
              />
            );
          }

          return null;
        })}
      </Box>
    </Box>
  );
}
